import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './UserAccess.css';

const UserAccess = ({ onBack, companySlug = "", companyName = "", companyModules = [], isSuperUser = false, currentUser = "" }) => {
    const PROTECTED_EMAILS = ['mraza@ups.com', 'shahzadahmed@ups.com'];
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newUserEmail, setNewUserEmail] = useState('');
    const [selectedModules, setSelectedModules] = useState([]);
    const [isSuper, setIsSuper] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [userCity, setUserCity] = useState('');
    const [glNumber, setGlNumber] = useState('');
    const [availableAccounts, setAvailableAccounts] = useState([]);
    const [selectedAccounts, setSelectedAccounts] = useState([]);
    const [availableImportAccounts, setAvailableImportAccounts] = useState([]);
    const [selectedImportAccounts, setSelectedImportAccounts] = useState([]);
    const [fcRoles, setFcRoles] = useState([]); // ['requester', 'approver']
    const [receiveEmails, setReceiveEmails] = useState(true);
    const [message, setMessage] = useState(null);

    const allModules = [
        { id: 'tracking', name: '📦 Tracking', description: 'Track UPS shipments' },
        { id: 'shipping', name: '🚢 Export Label', description: 'Create shipments and labels' },
        { id: 'bulk-shipping', name: '📑 Bulk Shipment', description: 'Generate bulk shipments from Excel' },
        { id: 'fc-label', name: '🏷️ Import Control Label', description: 'Create import control labels' },
        { id: 'request-fc', name: '📋 Request Base FC Module', description: 'Request and approve FC labels' },
        { id: 'rating', name: '💰 Rating', description: 'Get UPS rate quotes' },
        { id: 'search-zone', name: '🌍 Search Zone', description: 'Check remote areas and zones' },
        { id: 'address-validation', name: '✅ Address Validation', description: 'Validate U.S. addresses' },
        { id: 'settings', name: '⚙️ Settings', description: 'Manage system settings' },
        { id: 'user-access', name: '👥 User Access', description: 'Manage user permissions' },
        { id: 'billing', name: '💵 Billing', description: 'View shipment history and charges' },
        { id: 'reports', name: '📊 Reporting Analytics', description: 'Advanced business intelligence and sales reports' },
        { id: 'company', name: '🏢 Company Management', description: 'Create & manage companies (ERP)' },
        { id: 'view-all-shipments', name: '🔍 View All Shipments', description: 'See all users shipments' },
        { id: 'ops-security-scanning', name: '🛡️ OPS Security Scanning', description: 'Real-time barcode scanning and duplicate check' },
        { id: 'ops-receiving', name: '📦 OPS Receiving', description: 'Package receiving with barcode & label printing' },
    ];

    // Filter modules: Global super users see all. Company admins only see what the company has access to.
    const availableModules = isSuperUser && !companySlug ? allModules : allModules.filter(m => {
        // If it's a company-context, only show modules that the company has access to
        if (companySlug) {
            // Some modules like 'view-all-shipments' might be internal permissions rather than base company modules
            if (m.id === 'view-all-shipments') return true;
            return companyModules.includes(m.id);
        }
        return true;
    });

    const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:5001/api' : '/api';

    const fetchAvailableAccounts = useCallback(async () => {
        try {
            const endpoint = companySlug
                ? `${API_BASE}/companies/${companySlug}/tariffs`
                : `${API_BASE}/settings/tariffs`;
            const res = await axios.get(endpoint);
            if (res.data.success) {
                const uniqueAccounts = [...new Set(res.data.tariffs.map(t => t.account))].sort();
                setAvailableAccounts(uniqueAccounts);
            }

            // Also fetch import tariffs.
            const importEndpoint = companySlug
                ? `${API_BASE}/settings/import-tariffs?companySlug=${companySlug}`
                : `${API_BASE}/settings/import-tariffs`;
            const importRes = await axios.get(importEndpoint);
            if (importRes.data.success) {
                const uniqueImportAccounts = [...new Set(importRes.data.tariffs.map(t => t.account))].sort();
                setAvailableImportAccounts(uniqueImportAccounts);
            }
        } catch (err) {
            console.error('Error fetching accounts:', err);
        }
    }, [companySlug]);

    const fetchUsers = useCallback(async () => {
        try {
            const endpoint = companySlug
                ? `${API_BASE}/companies/${companySlug}/users`
                : `${API_BASE}/users`;
            const res = await axios.get(endpoint);
            if (res.data.success) {
                setUsers(res.data.users);
            }
        } catch (err) {
            console.error('Error fetching users:', err);
            showMessage('Failed to load users', 'error');
        } finally {
            setLoading(false);
        }
    }, [companySlug]);

    useEffect(() => {
        fetchUsers();
        fetchAvailableAccounts();
    }, [fetchUsers, fetchAvailableAccounts]);

    const showMessage = (text, type = 'success') => {
        setMessage({ text, type });
        setTimeout(() => setMessage(null), 3000);
    };

    const handleAddUser = async () => {
        if (!newUserEmail || selectedModules.length === 0) {
            showMessage('Please enter email and select at least one module', 'error');
            return;
        }

        try {
            const endpoint = companySlug
                ? `${API_BASE}/companies/${companySlug}/users/add`
                : `${API_BASE}/users/add`;
            const res = await axios.post(endpoint, {
                email: newUserEmail,
                modules: selectedModules,
                accounts: selectedAccounts,
                importAccounts: selectedImportAccounts,
                isSuperUser: !!isSuper && companySlug === "",
                city: userCity,
                glNumber: glNumber,
                companySlug: companySlug,
                fcRole: fcRoles,
                receiveEmails: receiveEmails
            });

            if (res.data.success) {
                showMessage('User added successfully');
                setNewUserEmail('');
                setUserCity('');
                setGlNumber('');
                setSelectedModules([]);
                setSelectedAccounts([]);
                setSelectedImportAccounts([]);
                setIsSuper(false);
                setShowAddModal(false);
                fetchUsers();
            }
        } catch (err) {
            showMessage(err.response?.data?.message || 'Failed to add user', 'error');
        }
    };

    const handleUpdateUser = async () => {
        if (!editingUser || selectedModules.length === 0) {
            showMessage('Please select at least one module', 'error');
            return;
        }

        try {
            const endpoint = companySlug
                ? `${API_BASE}/companies/${companySlug}/users/update`
                : `${API_BASE}/users/update`;
            const res = await axios.put(endpoint, {
                email: editingUser.email,
                modules: selectedModules,
                accounts: selectedAccounts,
                importAccounts: selectedImportAccounts,
                isSuperUser: !!isSuper && companySlug === "",
                city: userCity,
                glNumber: glNumber,
                fcRole: fcRoles,
                receiveEmails: receiveEmails
            });

            if (res.data.success) {
                showMessage('User permissions updated');
                setEditingUser(null);
                setSelectedModules([]);
                setSelectedAccounts([]);
                setSelectedImportAccounts([]);
                setGlNumber('');
                fetchUsers();
            }
        } catch (err) {
            showMessage(err.response?.data?.message || 'Failed to update user', 'error');
        }
    };

    const handleDeleteUser = async (email) => {
        if (!window.confirm(`Are you sure you want to delete user: ${email}?`)) {
            return;
        }

        try {
            const endpoint = companySlug
                ? `${API_BASE}/companies/${companySlug}/users/delete`
                : `${API_BASE}/users/delete`;
            const res = await axios.delete(endpoint, { data: { email } });

            if (res.data.success) {
                showMessage('User deleted successfully');
                fetchUsers();
            }
        } catch (err) {
            showMessage(err.response?.data?.message || 'Failed to delete user', 'error');
        }
    };

    const toggleModule = (moduleId) => {
        if (selectedModules.includes(moduleId)) {
            setSelectedModules(selectedModules.filter(m => m !== moduleId));
        } else {
            setSelectedModules([...selectedModules, moduleId]);
        }
    };

    const openEditModal = (user) => {
        setEditingUser(user);
        setSelectedModules(user.modules || []);
        // Filter out accounts that no longer exist in tariffs to prevent them from being re-saved
        const validAccounts = (user.accounts || []).filter(acc => acc === 'all' || availableAccounts.includes(acc));
        const validImportAccounts = (user.importAccounts || []).filter(acc => acc === 'all' || availableImportAccounts.includes(acc));

        setSelectedAccounts(validAccounts);
        setSelectedImportAccounts(validImportAccounts);
        setIsSuper(!!user.isSuperUser);
        setUserCity(user.city || '');
        setGlNumber(user.glNumber || '');
        setFcRoles(Array.isArray(user.fcRole) ? user.fcRole : (user.fcRole ? [user.fcRole] : []));
        setReceiveEmails(user.receiveEmails !== false);
    };

    const toggleAccount = (account) => {
        if (selectedAccounts.includes(account)) {
            setSelectedAccounts(selectedAccounts.filter(a => a !== account));
        } else {
            setSelectedAccounts([...selectedAccounts, account]);
        }
    };

    const toggleImportAccount = (account) => {
        if (selectedImportAccounts.includes(account)) {
            setSelectedImportAccounts(selectedImportAccounts.filter(a => a !== account));
        } else {
            setSelectedImportAccounts([...selectedImportAccounts, account]);
        }
    };

    return (
        <div className="user-access-container">
            <div className="user-access-header">
                <button className="back-btn" onClick={onBack}>←</button>
                <div>
                    <h2>👤 User Access Management</h2>
                    {companyName && <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>Company: <strong>{companyName}</strong></p>}
                </div>
            </div>

            {message && (
                <div className={`message-banner ${message.type}`}>
                    {message.text}
                </div>
            )}

            <div className="user-access-content">
                <div className="users-section">
                    <div className="section-header">
                        <h3>Registered Users</h3>
                        {(isSuperUser || companySlug) && (
                            <button className="add-user-btn" onClick={() => setShowAddModal(true)}>
                                + Add User
                            </button>
                        )}
                    </div>

                    {!isSuperUser && !companySlug && (
                        <div style={{ padding: '1rem', backgroundColor: '#fef3c7', borderRadius: '8px', marginBottom: '1rem', borderLeft: '4px solid #f59e0b' }}>
                            <p style={{ margin: 0, fontSize: '0.9rem', color: '#92400e' }}>
                                ℹ️ Only super users and company owners can manage users.
                            </p>
                        </div>
                    )}

                    {loading ? (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Loading users...</p>
                        </div>
                    ) : users.length === 0 ? (
                        <div className="empty-state">
                            <p>No users found. {(isSuperUser || companySlug) ? 'Add your first user to get started.' : 'Contact your administrator to request access.'}</p>
                        </div>
                    ) : (
                        <div className="users-list">
                            {users.map(user => (
                                <div key={user.email} className="user-card">
                                    <div className="user-info">
                                        <div className="user-email">
                                            {user.email}
                                            {user.isSuperUser && <span className="super-badge">SUPER USER</span>}
                                            {user.fcRole && user.fcRole.length > 0 && Array.isArray(user.fcRole) && user.fcRole.map(r => (
                                                <span key={r} className="role-badge" style={{ marginLeft: '10px', fontSize: '0.7rem', background: r === 'approver' ? '#dcfce7' : '#e0e7ff', color: r === 'approver' ? '#166534' : '#3730a3', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>{r.toUpperCase()}</span>
                                            ))}
                                            {/* Warn if request-fc module is assigned but no fc role set */}
                                            {user.modules?.includes('request-fc') && (!user.fcRole || user.fcRole.length === 0) && (
                                                <span title="This user has the Request FC module but no role (Requester/Approver) assigned. They cannot create or approve requests." style={{ marginLeft: '10px', fontSize: '0.7rem', background: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold', border: '1px solid #fcd34d', cursor: 'help' }}>
                                                    ⚠️ NO FC ROLE
                                                </span>
                                            )}
                                            {user.city && <span className="city-badge" style={{ marginLeft: '10px', fontSize: '0.7rem', background: '#e2e8f0', padding: '2px 6px', borderRadius: '4px' }}>📍 {user.city}</span>}
                                        </div>
                                        <div className="user-modules">
                                            {user.modules && user.modules.length > 0 ? (
                                                user.modules.map(mod => (
                                                    <span key={mod} className="module-badge">
                                                        {availableModules.find(m => m.id === mod)?.name || mod}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className="no-modules">No modules assigned</span>
                                            )}
                                        </div>
                                        <div className="user-accounts" style={{ marginTop: '5px' }}>
                                            {user.glNumber && (
                                                <span className="account-badge" style={{ marginRight: '5px', background: '#e2e8f0', color: '#1a202c', borderRadius: '4px', fontSize: '0.75rem' }}>
                                                    G/L: {user.glNumber}
                                                </span>
                                            )}
                                            {user.accounts && user.accounts.length > 0 ? (
                                                user.accounts.includes('all') ?
                                                    <span className="account-badge" style={{ background: '#fef3c7', color: '#92400e' }}>ALL ACCOUNTS</span> :
                                                    user.accounts
                                                        .filter(acc => availableAccounts.includes(acc)) // Filter out deleted accounts
                                                        .map(acc => (
                                                            <span key={acc} className="account-badge" style={{ marginRight: '5px', padding: '2px 6px', background: '#dcfce7', color: '#166534', borderRadius: '4px', fontSize: '0.75rem' }}>
                                                                🆔 {acc}
                                                            </span>
                                                        ))
                                            ) : null}

                                            {/* Also show import accounts if any */}
                                            {user.importAccounts && user.importAccounts.length > 0 && (
                                                user.importAccounts
                                                    .filter(acc => availableImportAccounts.includes(acc)) // Filter out deleted accounts
                                                    .map(acc => (
                                                        <span key={acc} className="account-badge" style={{ marginRight: '5px', padding: '2px 6px', background: '#e0f2fe', color: '#0369a1', borderRadius: '4px', fontSize: '0.75rem' }}>
                                                            ✈️ {acc}
                                                        </span>
                                                    ))
                                            )}

                                            {(!user.accounts || user.accounts.length === 0) && (!user.importAccounts || user.importAccounts.length === 0) && (
                                                <span className="no-modules" style={{ fontSize: '0.75rem' }}>No accounts assigned</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="user-actions">
                                        <button
                                            className="edit-btn"
                                            onClick={() => openEditModal(user)}
                                            disabled={(!isSuperUser && !companySlug)}
                                            title={(!isSuperUser && !companySlug) ? 'No permission to edit' : 'Edit'}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            className="delete-btn"
                                            onClick={() => handleDeleteUser(user.email)}
                                            disabled={PROTECTED_EMAILS.includes(user.email) || (!isSuperUser && !companySlug)}
                                            title={PROTECTED_EMAILS.includes(user.email) ? 'This user cannot be deleted' : (!isSuperUser && !companySlug) ? 'No permission to delete' : 'Delete'}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Add User Modal */}
            {showAddModal && (
                <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Add New User {companyName && `to ${companyName}`}</h3>
                        <div className="form-group">
                            <label>Email Address</label>
                            <input
                                type="email"
                                placeholder="user@ups.com"
                                value={newUserEmail}
                                onChange={(e) => setNewUserEmail(e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Location (City Code e.g. PKLHE)</label>
                            <input
                                type="text"
                                placeholder="PKLHE"
                                value={userCity}
                                onChange={(e) => setUserCity(e.target.value.toUpperCase())}
                            />
                        </div>
                        <div className="form-group">
                            <label>G/L Number</label>
                            <input
                                type="text"
                                placeholder="Optional"
                                value={glNumber}
                                onChange={(e) => setGlNumber(e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Select Modules</label>
                            <div className="modules-grid">
                                {availableModules.map(module => (
                                    <div
                                        key={module.id}
                                        className={`module-option ${selectedModules.includes(module.id) ? 'selected' : ''}`}
                                        onClick={() => toggleModule(module.id)}
                                    >
                                        <div className="module-name">{module.name}</div>
                                        <div className="module-desc">{module.description}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Select ICRIS Accounts</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '5px' }}>
                                {availableAccounts.map(acc => (
                                    <div
                                        key={acc}
                                        onClick={() => toggleAccount(acc)}
                                        style={{
                                            padding: '4px 10px',
                                            borderRadius: '20px',
                                            border: '1px solid #cbd5e0',
                                            cursor: 'pointer',
                                            fontSize: '0.85rem',
                                            background: selectedAccounts.includes(acc) ? '#3182ce' : '#fff',
                                            color: selectedAccounts.includes(acc) ? '#fff' : '#4a5568',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        {acc}
                                    </div>
                                ))}
                                {availableAccounts.length === 0 && <span style={{ fontSize: '0.85rem', color: '#718096' }}>No accounts found in tariffs</span>}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Select Import Accounts (for FC Label)</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '5px' }}>
                                {availableImportAccounts.map(acc => (
                                    <div
                                        key={acc}
                                        onClick={() => toggleImportAccount(acc)}
                                        style={{
                                            padding: '4px 10px',
                                            borderRadius: '20px',
                                            border: '1px solid #cbd5e0',
                                            cursor: 'pointer',
                                            fontSize: '0.85rem',
                                            background: selectedImportAccounts.includes(acc) ? '#8b5cf6' : '#fff',
                                            color: selectedImportAccounts.includes(acc) ? '#fff' : '#4a5568',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        {acc}
                                    </div>
                                ))}
                                {availableImportAccounts.length === 0 && <span style={{ fontSize: '0.85rem', color: '#718096' }}>No accounts found in import tariffs</span>}
                            </div>
                        </div>
                        {selectedModules.includes('request-fc') && (
                            <div className="form-group">
                                <label>Request Base FC Roles</label>
                                <div style={{ display: 'flex', gap: '15px', marginTop: '5px' }}>
                                    <label style={{ fontWeight: 'normal', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={fcRoles.includes('requester')}
                                            onChange={(e) => {
                                                if (e.target.checked) setFcRoles([...fcRoles, 'requester']);
                                                else setFcRoles(fcRoles.filter(r => r !== 'requester'));
                                            }}
                                        /> Requester
                                    </label>
                                    <label style={{ fontWeight: 'normal', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={fcRoles.includes('approver')}
                                            onChange={(e) => {
                                                if (e.target.checked) setFcRoles([...fcRoles, 'approver']);
                                                else setFcRoles(fcRoles.filter(r => r !== 'approver'));
                                            }}
                                        /> Approver
                                    </label>
                                </div>
                                <div style={{ marginTop: '8px', padding: '8px 10px', background: '#fef3c7', borderRadius: '6px', border: '1px solid #fcd34d', fontSize: '0.78rem', color: '#92400e', lineHeight: '1.5' }}>
                                    ⚠️ <strong>Important:</strong> <em>Requester</em> allows the user to create new FC label requests.
                                    If unchecked, the user will <strong>not</strong> be able to submit new requests.
                                    <em>Approver</em> allows reviewing and approving requests — approvers can also create requests.
                                </div>
                            </div>
                        )}
                        <div className="form-group">
                            <label>
                                <input type="checkbox" checked={receiveEmails} onChange={(e) => setReceiveEmails(e.target.checked)} />
                                {' '}Receive Email Notifications
                            </label>
                        </div>
                        {!companySlug && (
                            <div className="form-group">
                                <label>
                                    <input type="checkbox" checked={isSuper} onChange={(e) => setIsSuper(e.target.checked)} />
                                    {' '}Grant Super User (full access)
                                </label>
                            </div>
                        )}
                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={() => {
                                setShowAddModal(false);
                                setNewUserEmail('');
                                setUserCity('');
                                setUserCity('');
                                setSelectedModules([]);
                                setSelectedAccounts([]);
                                setSelectedImportAccounts([]);
                                setFcRoles([]);
                                setIsSuper(false);
                            }}>
                                Cancel
                            </button>
                            <button className="save-btn" onClick={handleAddUser}>
                                Add User
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit User Modal */}
            {editingUser && (
                <div className="modal-overlay" onClick={() => setEditingUser(null)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Edit User Permissions</h3>
                        <div className="form-group">
                            <label>Email Address</label>
                            <input
                                type="email"
                                value={editingUser.email}
                                disabled
                            />
                        </div>
                        <div className="form-group">
                            <label>Location (City Code)</label>
                            <input
                                type="text"
                                value={userCity}
                                onChange={(e) => setUserCity(e.target.value.toUpperCase())}
                            />
                        </div>
                        <div className="form-group">
                            <label>G/L Number</label>
                            <input
                                type="text"
                                value={glNumber}
                                onChange={(e) => setGlNumber(e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Select Modules</label>
                            <div className="modules-grid">
                                {availableModules.map(module => (
                                    <div
                                        key={module.id}
                                        className={`module-option ${selectedModules.includes(module.id) ? 'selected' : ''}`}
                                        onClick={() => toggleModule(module.id)}
                                    >
                                        <div className="module-name">{module.name}</div>
                                        <div className="module-desc">{module.description}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Select ICRIS Accounts</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '5px' }}>
                                {availableAccounts.map(acc => (
                                    <div
                                        key={acc}
                                        onClick={() => toggleAccount(acc)}
                                        style={{
                                            padding: '4px 10px',
                                            borderRadius: '20px',
                                            border: '1px solid #cbd5e0',
                                            cursor: 'pointer',
                                            fontSize: '0.85rem',
                                            background: selectedAccounts.includes(acc) ? '#3182ce' : '#fff',
                                            color: selectedAccounts.includes(acc) ? '#fff' : '#4a5568',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        {acc}
                                    </div>
                                ))}
                                {availableAccounts.length === 0 && <span style={{ fontSize: '0.85rem', color: '#718096' }}>No accounts found in tariffs</span>}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Select Import Accounts (for FC Label)</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '5px' }}>
                                {availableImportAccounts.map(acc => (
                                    <div
                                        key={acc}
                                        onClick={() => toggleImportAccount(acc)}
                                        style={{
                                            padding: '4px 10px',
                                            borderRadius: '20px',
                                            border: '1px solid #cbd5e0',
                                            cursor: 'pointer',
                                            fontSize: '0.85rem',
                                            background: selectedImportAccounts.includes(acc) ? '#8b5cf6' : '#fff',
                                            color: selectedImportAccounts.includes(acc) ? '#fff' : '#4a5568',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        {acc}
                                    </div>
                                ))}
                                {availableImportAccounts.length === 0 && <span style={{ fontSize: '0.85rem', color: '#718096' }}>No accounts found in import tariffs</span>}
                            </div>
                        </div>
                        {selectedModules.includes('request-fc') && (
                            <div className="form-group">
                                <label>Request Base FC Roles</label>
                                <div style={{ display: 'flex', gap: '15px', marginTop: '5px' }}>
                                    <label style={{ fontWeight: 'normal', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={fcRoles.includes('requester')}
                                            onChange={(e) => {
                                                if (e.target.checked) setFcRoles([...fcRoles, 'requester']);
                                                else setFcRoles(fcRoles.filter(r => r !== 'requester'));
                                            }}
                                        /> Requester
                                    </label>
                                    <label style={{ fontWeight: 'normal', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={fcRoles.includes('approver')}
                                            onChange={(e) => {
                                                if (e.target.checked) setFcRoles([...fcRoles, 'approver']);
                                                else setFcRoles(fcRoles.filter(r => r !== 'approver'));
                                            }}
                                        /> Approver
                                    </label>
                                </div>
                                <div style={{ marginTop: '8px', padding: '8px 10px', background: '#fef3c7', borderRadius: '6px', border: '1px solid #fcd34d', fontSize: '0.78rem', color: '#92400e', lineHeight: '1.5' }}>
                                    ⚠️ <strong>Important:</strong> <em>Requester</em> allows the user to create new FC label requests.
                                    If unchecked, the user will <strong>not</strong> be able to submit new requests.
                                    <em>Approver</em> allows reviewing and approving requests — approvers can also create requests.
                                </div>
                            </div>
                        )}
                        <div className="form-group">
                            <label>
                                <input type="checkbox" checked={receiveEmails} onChange={(e) => setReceiveEmails(e.target.checked)} />
                                {' '}Receive Email Notifications
                            </label>
                        </div>
                        {!companySlug && (
                            <div className="form-group">
                                <label>
                                    <input type="checkbox" checked={isSuper} onChange={(e) => setIsSuper(e.target.checked)} />
                                    {' '}Grant Super User (full access)
                                </label>
                            </div>
                        )}
                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={() => {
                                setEditingUser(null);
                                setUserCity('');
                                setSelectedModules([]);
                                setSelectedAccounts([]);
                                setSelectedImportAccounts([]);
                                setGlNumber('');
                                setFcRoles([]);
                            }}>
                                Cancel
                            </button>
                            <button className="save-btn" onClick={handleUpdateUser}>
                                Update Permissions
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserAccess;



